import { AlertTriangle, Pencil, Trash } from "@tamagui/lucide-icons";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import {
  useSensors,
  useUserRole,
  Sensor,
  SensorStatus,
  SensorType,
} from "common";
import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useNavigate } from "react-router";
import {
  YStack,
  XStack,
  Text,
  Spinner,
  Card,
  Separator,
  ScrollView,
  Button,
  View,
  useMedia,
} from "tamagui";
import { Table } from "tamagui-config";

import SensorFormModal, {
  FormValues as CreateSensorFormValues,
} from "./SensorFormModal";
import ConfirmDialog from "../../components/ConfirmDialog";
import { useSensorType } from "../../hooks/useSensorType";

const StatusIndicator = ({ status }: { status: string }) => (
  <View
    flexDirection="row"
    alignItems="center"
    gap="$2"
    theme={status?.toLowerCase() === "online" ? "green" : "orange"}
    backgroundColor="$color6"
    borderRadius={1000_000_000}
    paddingHorizontal="$2"
    paddingVertical="$1"
  >
    <View
      width={10}
      height={10}
      backgroundColor="$color10"
      borderRadius={1000}
    />
    <Text color="$color10" fontWeight="$2">
      {status}
    </Text>
  </View>
);

const ActionView = ({
  sensor,
  onSensorDelete,
  onSensorEdit,
}: {
  sensor: Sensor;
  onSensorDelete: (sensor: Sensor) => void;
  onSensorEdit: (sensor: Sensor) => void;
}) => {
  const { permissions } = useUserRole();

  return (
    <View flexDirection="row" gap="$2" onPress={(e) => e.stopPropagation()}>
      <Button
        disabled={!permissions.sensor.update}
        icon={<Pencil size="$1" />}
        onPress={(e) => {
          e.stopPropagation();
          onSensorEdit(sensor);
        }}
        disabledStyle={{
          cursor: "default",
          opacity: 0.5,
        }}
      ></Button>
      <Button
        disabled={!permissions.sensor.delete}
        icon={<Trash size="$1" />}
        onPress={(e) => {
          e.stopPropagation();
          onSensorDelete(sensor);
        }}
        disabledStyle={{
          cursor: "default",
          opacity: 0.5,
        }}
      ></Button>
    </View>
  );
};

const formatTimestamp = (timestamp: number) => {
  if (!timestamp) return "N/A";

  const date = new Date(timestamp * 1000);
  return date.toLocaleString();
};

const Sensors: React.FC = () => {
  const { sm, xs } = useMedia();
  const rowCounter = useRef(-1);
  const { t } = useTranslation();
  const { hubId } = useParams<{ hubId: string }>();
  const navigate = useNavigate();
  const {
    fetchSensorsByHub,
    addSensor,
    updateSensor,
    removeSensor,
    sensors,
    hub,
    loading,
    error,
  } = useSensors(hubId);
  const { permissions } = useUserRole();
  const { translateSensorType } = useSensorType();
  const [isCreateDialogOpen, setCreateDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedSensor, setSelectedSensor] = useState<Sensor | null>(null);

  useEffect(() => {
    if (!hubId) {
      navigate("/base-stations");
    }
  }, [hubId]);

  const handleSensorClick = (sensorId: string) => {
    navigate(`/base-stations/${hubId}/sensors/${sensorId}/events`);
  };

  const onSensorDelete = (sensor: Sensor) => {
    setSelectedSensor(sensor);
    setDeleteDialogOpen(true);
  };

  const onSensorEdit = (sensor: Sensor) => {
    setSelectedSensor(sensor);
    setCreateDialogOpen(true);
  };

  const onSensorAdd = () => {
    setSelectedSensor(null);
    setCreateDialogOpen(true);
  };

  const handleSensorSubmit = async (values: CreateSensorFormValues) => {
    const payload = {
      hubId: values.hubId,
      name: values.name,
      location: values.location,
      dsk: values.dsk,
      type: values.type,
      version: values.version,
      serialNumber: values.serialNumber,
      status: values.status,
    };
    if (selectedSensor) {
      await updateSensor({ id: selectedSensor.id, ...payload });
    } else {
      await addSensor(payload);
    }
    setSelectedSensor(null);
  };

  const handleDeleteSensorSubmit = async (sensor: Sensor) => {
    await removeSensor(sensor.id);
    setSelectedSensor(null);
  };

  const transformStatusValue = (status: string): string => {
    switch (status.toUpperCase()) {
      case SensorStatus.Active:
        return t("sensors.statusValue.active");
      case SensorStatus.Inactive:
        return t("sensors.statusValue.inactive");
      case SensorStatus.Maintenance:
        return t("sensors.statusValue.maintenance");
      case SensorStatus.Pending:
        return t("sensors.statusValue.pending");
      default:
        return t("sensors.statusValue.unknown");
    }
  };

  const columnHelper = createColumnHelper<Sensor>();

  const columns = [
    columnHelper.accessor("name", {
      header: () => t("sensors.table.name"),
      cell: (info) => <Text>{info.getValue()}</Text>,
    }),
    columnHelper.accessor("location", {
      header: () => t("sensors.table.location"),
      cell: (info) => <Text>{info.getValue()}</Text>,
    }),
    columnHelper.accessor("type", {
      header: () => t("sensors.table.type"),
      cell: (info) => (
        <Text>{translateSensorType(info.getValue() as SensorType)}</Text>
      ),
    }),
    columnHelper.accessor("serialNumber", {
      header: () => t("sensors.table.serialNumber"),
      cell: (info) => <Text>{info.getValue()}</Text>,
    }),
    columnHelper.accessor("status", {
      header: () => t("sensors.table.status"),
      cell: (info) => (
        <StatusIndicator status={transformStatusValue(info.getValue())} />
      ),
    }),
    columnHelper.accessor("lastReported", {
      header: () => t("sensors.table.lastReported"),
      cell: (info) => <Text>{formatTimestamp(info.getValue())}</Text>,
    }),
    columnHelper.display({
      id: "actions",
      header: () => t("sensors.table.action"),
      cell: ({ row }) => (
        <ActionView
          sensor={row.original}
          onSensorDelete={onSensorDelete}
          onSensorEdit={onSensorEdit}
        />
      ),
    }),
  ];

  const table = useReactTable({
    data: sensors,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  const renderContent = () => {
    if (loading) {
      return (
        <YStack flex={1} justifyContent="center" alignItems="center" space="$4">
          <Spinner size="large" />
          <Text>{t("sensors.loading")}</Text>
        </YStack>
      );
    }

    if (error) {
      return (
        <YStack
          flex={1}
          justifyContent="center"
          alignItems="center"
          padding="$4"
        >
          <AlertTriangle size={48} color="$red10" />
          <Text marginTop="$4" color="$red10">
            {error?.message || t("common.error")}
          </Text>
          <Button
            marginTop="$4"
            onPress={() => hubId && fetchSensorsByHub(hubId)}
          >
            {t("common.retry")}
          </Button>
        </YStack>
      );
    }

    if (!sensors || sensors.length === 0) {
      return (
        <YStack flex={1} justifyContent="center" alignItems="center">
          <Text>{t("sensors.noSensors")}</Text>
          <Button
            marginTop="$4"
            onPress={() => hubId && fetchSensorsByHub(hubId)}
          >
            {t("common.refresh")}
          </Button>
        </YStack>
      );
    }

    const headerGroups = table.getHeaderGroups();
    const tableRows = table.getRowModel().rows;
    const footerGroups = table.getFooterGroups();

    const allRowsLength =
      tableRows.length + headerGroups.length + footerGroups.length;
    rowCounter.current = -1;

    if (sm || xs) {
      return (
        <View
          width="100%"
          flexDirection="column"
          justifyContent="center"
          gap="$5"
          paddingVertical="$6"
        >
          {sensors.map((sensor) => (
            <View
              key={sensor.id}
              borderRadius="$4"
              borderWidth="$1"
              borderColor="$borderColor"
              flex={1}
              alignSelf="stretch"
              width="100%"
              gap="$2"
              paddingTop="$2"
              pressStyle={{ opacity: 0.8, scale: 0.98 }}
              onPress={() => handleSensorClick(sensor.id)}
            >
              <XStack
                alignItems="center"
                paddingVertical="$1"
                marginLeft="$3"
                gap="$2"
              >
                <View justifyContent="space-between">
                  <Text>{sensor.name || t("sensors.unnamed")}</Text>
                  <Text fontSize="$3" color="$gray11">
                    {sensor.serialNumber}
                  </Text>
                </View>
                <View marginLeft="auto" paddingRight="$3">
                  <StatusIndicator status={sensor.status} />
                </View>
              </XStack>

              <View height={2} backgroundColor="$borderColor" />

              <View gap="$2">
                {Object.entries(sensor)
                  .filter(
                    ([name]) =>
                      !["name", "status", "serialNumber"].includes(name),
                  )
                  .map(([name, value], i, arr) => (
                    <View key={name}>
                      <View
                        flexDirection="row"
                        justifyContent="space-between"
                        marginHorizontal="$3"
                        paddingBottom="$2"
                      >
                        <Text>
                          {name.charAt(0).toUpperCase() + name.slice(1)}
                        </Text>
                        <Text color="$gray11">
                          {name === "lastReported"
                            ? formatTimestamp(value as number)
                            : (value as string)}
                        </Text>
                      </View>
                      {i !== arr.length - 1 && <Separator />}
                    </View>
                  ))}
              </View>
            </View>
          ))}
        </View>
      );
    }

    return (
      <Table
        alignCells={{ x: "flex-start", y: "center" }}
        alignHeaderCells={{ y: "center", x: "flex-start" }}
        cellWidth="$18"
        cellHeight="$7"
        borderWidth={0}
        padding="$4"
        maxWidth="100%"
        maxHeight={600}
        gap="$5"
      >
        <Table.Head>
          {headerGroups.map((headerGroup) => {
            rowCounter.current++;
            return (
              <Table.Row
                rowLocation={
                  rowCounter.current === 0
                    ? "first"
                    : rowCounter.current === allRowsLength - 1
                      ? "last"
                      : "middle"
                }
                key={headerGroup.id}
                justifyContent="flex-start"
              >
                {headerGroup.headers.map((header, index) => (
                  <Table.HeaderCell
                    cellLocation={
                      index === 0
                        ? "first"
                        : index === headerGroup.headers.length - 1
                          ? "last"
                          : "middle"
                    }
                    key={header.id}
                    borderWidth={0}
                    justifyContent="flex-start"
                    flexShrink={header.column.id === "id" ? 3 : 1}
                  >
                    <Text fontWeight="bold">
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext(),
                          )}
                    </Text>
                  </Table.HeaderCell>
                ))}
              </Table.Row>
            );
          })}
        </Table.Head>
        <Table.Body>
          {tableRows.map((row) => {
            rowCounter.current++;
            return (
              <Table.Row
                hoverStyle={{
                  backgroundColor: "$color2",
                  cursor: "pointer",
                }}
                pressStyle={{
                  backgroundColor: "$color3",
                }}
                onPress={() => handleSensorClick(row.original.id)}
                rowLocation={
                  rowCounter.current === 0
                    ? "first"
                    : rowCounter.current === allRowsLength - 1
                      ? "last"
                      : "middle"
                }
                key={row.id}
              >
                {row.getVisibleCells().map((cell, index) => (
                  <Table.Cell
                    cellLocation={
                      index === 0
                        ? "first"
                        : index === row.getVisibleCells().length - 1
                          ? "last"
                          : "middle"
                    }
                    key={cell.id}
                    borderWidth={0}
                    justifyContent="flex-start"
                    flexShrink={cell.column.id === "id" ? 3 : 1}
                  >
                    {cell.column.id === "status" ? (
                      flexRender(cell.column.columnDef.cell, cell.getContext())
                    ) : (
                      <Text theme="alt1">
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext(),
                        )}
                      </Text>
                    )}
                  </Table.Cell>
                ))}
              </Table.Row>
            );
          })}
        </Table.Body>
      </Table>
    );
  };

  if (!hubId) {
    return;
  }

  return (
    <YStack flex={1} padding="$4">
      <XStack justifyContent="space-between">
        <XStack alignItems="center" space="$2">
          <Button
            size="$3"
            chromeless
            onPress={() => navigate("/base-stations")}
            icon={<Text fontSize="$6">←</Text>}
          />
          <YStack>
            <Text fontSize="$8" fontWeight="bold">
              {t("sensors.title")}
            </Text>
            {hub && (
              <Text fontSize="$3" color="$gray11">
                {t("sensors.forHub", { hubName: hub.name || hub.serialNumber })}
              </Text>
            )}
          </YStack>
        </XStack>
        <Button
          onPress={onSensorAdd}
          backgroundColor="$hgPrimary"
          size={"$4"}
          disabled={!permissions.sensor.create}
        >
          {t("sensors.addSensor")}
        </Button>
      </XStack>
      <Separator marginTop="$4" />

      <Card flex={1} marginTop="$4" padding="$4" overflow="hidden">
        <ScrollView>{renderContent()}</ScrollView>
      </Card>

      <SensorFormModal
        open={isCreateDialogOpen}
        onOpenChange={setCreateDialogOpen}
        onSubmit={handleSensorSubmit}
        sensor={selectedSensor}
        hubId={hubId}
      ></SensorFormModal>

      <ConfirmDialog
        open={isDeleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        onConfirm={() => handleDeleteSensorSubmit(selectedSensor as Sensor)}
        title={t("sensors.confirm.title")}
        description={t("sensors.confirm.description")}
      ></ConfirmDialog>
    </YStack>
  );
};

export default Sensors;
